import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Download, Plus } from "@/components/icons";
import { Button, Input, toast } from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { ReorderPlanQueryDTO } from "@/modules/inventory/domain/contracts";
import type { ReorderPlanRowDTO } from "@/modules/inventory/domain/types";
import type { ReportProductFilterDTO } from "@/modules/reports/domain/contracts";
import {
  buildDateRangePreset,
  reportRangeToInclusiveEnd,
} from "@/modules/reports/domain/date-range-presets";
import { ReportProductVariantFilter } from "@/modules/reports/presentation/ui/ReportProductVariantFilter";
import { ReportsDateRange } from "@/modules/reports/presentation/ui/ReportsDateRange";
import { config } from "@/shared/lib/config";
import { useReorderPlanQuery } from "../api/queries";
import { saveImportOrderPhase13Prefill } from "../lib/import-prefill-phase13";
import { enrichImportLinesWithLastPrices } from "../lib/apply-last-import-prices";
import { InventoryPlanningPanel } from "../ui/InventoryPlanningPanel";
import type { ImportOrderPhase13LineForm } from "../ui/ImportOrderPhase13Form";
import { ReorderPlanTable } from "../ui/ReorderPlanTable";

export function PlanningPage() {
  const nav = useNavigate();
  const canWrite = useActionPermission("inventory:write");
  const [range, setRange] = useState(() => buildDateRangePreset("7d"));
  const [activePreset, setActivePreset] = useState<
    "today" | "7d" | "month" | "custom"
  >("7d");
  const [targetDaysInput, setTargetDaysInput] = useState("30");
  const [productFilter, setProductFilter] = useState<ReportProductFilterDTO>(
    {},
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [productFilter.productId, productFilter.variantId]);

  const targetDaysOfCover = Number.parseInt(targetDaysInput, 10);
  const query: ReorderPlanQueryDTO = {
    from: range.from,
    to: range.to,
    targetDaysOfCover:
      Number.isFinite(targetDaysOfCover) && targetDaysOfCover > 0
        ? targetDaysOfCover
        : undefined,
    limit: 100,
    productId: productFilter.productId,
    variantId: productFilter.variantId,
  };

  const plan = useReorderPlanQuery(query);
  const rows = plan.data?.data ?? [];

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.has(row.variantId)),
    [rows, selectedIds],
  );

  function toggleRow(variantId: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(variantId);
      else next.delete(variantId);
      return next;
    });
  }

  function toggleAll(checked: boolean) {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(
      new Set(
        rows
          .filter((row) => row.suggestedOrderQty > 0)
          .map((row) => row.variantId),
      ),
    );
  }

  function buildImportLines(
    fromRows: ReorderPlanRowDTO[],
  ): ImportOrderPhase13LineForm[] {
    return fromRows
      .filter((row) => row.suggestedOrderQty > 0)
      .map((row) => ({
        variantId: row.variantId,
        productName: row.productName,
        variantName: row.variantName,
        imageKey: row.imageKey,
        description: "",
        orderedQuantity: row.suggestedOrderQty,
        unitPriceForeign: 0,
      }));
  }

  function createImportFromSelection() {
    void (async () => {
      const lines = buildImportLines(selectedRows);
      if (lines.length === 0) {
        toast.error("ເລືອກລາຍການທີ່ມີຈຳນວນແນະນຳກ່ອນ");
        return;
      }
      try {
        const enriched = await enrichImportLinesWithLastPrices(lines);
        saveImportOrderPhase13Prefill(enriched);
        nav({ to: "/app/import-orders/new" });
      } catch {
        toast.error("ໂຫຼດລາຄາຄັ້ງກ່ອນບໍ່ສຳເລັດ");
      }
    })();
  }

  async function exportCsv() {
    setExporting(true);
    try {
      const url = new URL(`${config.apiUrl}/inventory/planning/reorder/export`);
      if (query.from) url.searchParams.set("from", query.from);
      if (query.to) url.searchParams.set("to", query.to);
      if (query.targetDaysOfCover) {
        url.searchParams.set(
          "targetDaysOfCover",
          String(query.targetDaysOfCover),
        );
      }
      if (query.limit) url.searchParams.set("limit", String(query.limit));
      if (query.productId) url.searchParams.set("productId", query.productId);
      if (query.variantId) url.searchParams.set("variantId", query.variantId);

      const res = await fetch(url.toString(), { credentials: "include" });
      if (!res.ok) throw new Error("EXPORT_FAILED");

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `reorder-plan-${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error("ສົ່ງອອກ CSV ບໍ່ສຳເລັດ");
    } finally {
      setExporting(false);
    }
  }

  const fromLabel = new Date(range.from).toLocaleDateString("lo-LA");
  const toLabel = reportRangeToInclusiveEnd(range.to).toLocaleDateString(
    "lo-LA",
  );

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ວາງແຜນສັ່ງຂອງ
            </h2>
            <p className="text-muted-foreground text-sm">
              ຄຳນວນຈາກຍອດຂາຍ + ສຕັອກ + ຂອງລໍຖ້າ — ແນະນຳຈຳນວນສັ່ງຕໍ່ variant
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="rounded-full"
              disabled={exporting || plan.isLoading}
              onClick={() => void exportCsv()}
            >
              <Download data-icon="inline-start" />
              {exporting ? "ກຳລັງສົ່ງອອກ..." : "CSV"}
            </Button>
            {canWrite ? (
              <Button
                className="rounded-full"
                disabled={selectedRows.length === 0}
                onClick={createImportFromSelection}
              >
                <Plus data-icon="inline-start" />
                ສ້າງ Import ({selectedRows.length})
              </Button>
            ) : null}
          </div>
        </div>

        <ReportsDateRange
          range={range}
          activePreset={activePreset}
          onPresetChange={(preset) => {
            setActivePreset(preset);
            if (preset !== "custom") {
              setRange(buildDateRangePreset(preset));
            }
          }}
          onBasisChange={(basis) => setRange((r) => ({ ...r, basis }))}
          onCustomRangeChange={(next) => {
            setActivePreset("custom");
            setRange(next);
          }}
        />

        <ReportProductVariantFilter
          value={productFilter}
          onChange={setProductFilter}
        />

        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="target-days-cover"
              className="font-medium text-foreground text-sm"
            >
              ເປົ້າເກັບສຕັອກ (ວັນ)
            </label>
            <Input
              id="target-days-cover"
              type="number"
              min={1}
              max={365}
              className="w-28 rounded-full"
              value={targetDaysInput}
              onChange={(e) => setTargetDaysInput(e.target.value)}
            />
          </div>
          {plan.data?.meta ? (
            <p className="text-muted-foreground text-sm">
              {fromLabel} – {toLabel} · ພົບ {plan.data.meta.total} ລາຍການ (
              {plan.data.meta.criticalCount} ດ່ວນ, {plan.data.meta.soonCount}{" "}
              ໃກ້ຫມົດ)
            </p>
          ) : null}
        </div>

        <ReorderPlanTable
          rows={rows}
          isLoading={plan.isLoading}
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
        />

        <InventoryPlanningPanel listLimit={3} showHeading={false} />
      </Main>
    </>
  );
}
